// Which of a grid's columns go where on a phone's card. Pure: Head entries in,
// slots out.
//
// A Head entry may say where it belongs; none has to, the cell type decides
// otherwise:
//
//   mobilePriority: 'primary' | 'status' | 'secondary' | 'headline'
//   mobileHidden:   true — never picked for a card
//
// Slots, top to bottom:
//   identity  — the record's name or id, first in the header line
//   statuses  — up to MAX_STATUS_CHIPS labels beside it
//   secondary — up to MAX_SECONDARY_FIELDS label-over-value pairs
//   headline  — the large value in the footer (an amount)
//   meta      — the footer's date
// Columns left out are in the record's details and editor.

const MAX_STATUS_CHIPS = 2;
const MAX_SECONDARY_FIELDS = 3;

const STATUS_CELLS = ['Status', 'State'];
const HEADLINE_CELLS = ['Amount', 'CryptoAmount', 'Balance'];
const META_CELLS = ['DateTime', 'Date', 'CreateModify'];
const IDENTITY_CELLS = [undefined, 'Text', 'Url', 'Entity', 'Number'];

const byPriority = (fields, priority) =>
    fields.filter((field) => field.mobilePriority === priority);

const isCandidate = (field, used) =>
    !used.has(field) && !field.mobileHidden && !field.mobilePriority;

/**
 * Lays a grid's visible columns out on a card.
 *
 * @param {Array} fields Head entries the grid shows, in Head order
 * @returns {{identity, statuses, secondary, headline, meta}} `identity`,
 *   `headline` and `meta` are Head entries or undefined; `statuses` and
 *   `secondary` arrays of them, possibly empty
 */
export function buildCardLayout(fields) {
    const used = new Set();
    const take = (field) => {
        if (field) used.add(field);
        return field;
    };
    const firstCandidate = (test) =>
        take(fields.find((field) => isCandidate(field, used) && test(field)));

    // What a Head entry asks for first, then the cell type fills the rest.
    const identity = take(byPriority(fields, 'primary')[0]);
    const statuses = byPriority(fields, 'status')
        .slice(0, MAX_STATUS_CHIPS)
        .map(take);
    const headline = take(byPriority(fields, 'headline')[0]);
    const secondary = byPriority(fields, 'secondary')
        .slice(0, MAX_SECONDARY_FIELDS)
        .map(take);

    const resolvedIdentity =
        identity ||
        firstCandidate(
            (field) => field.id === 'id' || IDENTITY_CELLS.includes(field.cell)
        );

    const resolvedStatuses = statuses.length
        ? statuses
        : fields
            .filter(
                (field) =>
                    isCandidate(field, used) &&
                    STATUS_CELLS.includes(field.cell)
            )
            .slice(0, MAX_STATUS_CHIPS)
            .map(take);

    const resolvedHeadline =
        headline ||
        firstCandidate((field) => HEADLINE_CELLS.includes(field.cell));

    const meta = firstCandidate((field) => META_CELLS.includes(field.cell));

    const resolvedSecondary = secondary.length
        ? secondary
        : fields
            .filter((field) => isCandidate(field, used))
            .slice(0, MAX_SECONDARY_FIELDS)
            .map(take);

    return {
        identity: resolvedIdentity,
        statuses: resolvedStatuses,
        secondary: resolvedSecondary,
        headline: resolvedHeadline,
        meta
    };
}
